'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Store del carrito NQORO.
 *
 * Estrategia:
 *  - El carrito vive 100% en el cliente (Zustand + localStorage persist) hasta
 *    iniciar checkout. Eso habilita navegación rápida sin round-trips al server.
 *  - En checkout, el cliente POST-ea el estado al server, que crea un `carts` +
 *    `stock_reservations` (TTL 15 min) y redirige a /checkout/envio.
 *  - Al login, el merge con el carrito servidor se hace via `/api/cart/merge`
 *    (implementa FASE 8 chunk 2).
 *
 * Las snapshots de precio se toman al momento de añadir — si el admin cambia
 * el precio después, el cliente conserva el precio original en el cart y el
 * server re-valida en checkout (puede avisar si cambió).
 */

export interface CartItem {
  /** variantId — único por línea, no se duplican, se suma quantity */
  variantId: string;
  productId: string;
  productSlug: string;
  collectionSlug: string;
  productNameEs: string;
  axes: Record<string, string>; // para mostrar "Talla M · Color Noir"
  sku: string;
  quantity: number;
  /** Precio unitario en centavos MXN (IVA incluido) */
  unitPriceMxnCents: number;
  /** Precio comparativo si hay descuento */
  unitPriceMxnCentsCompare: number | null;
  /** Cloudinary publicId de la imagen de portada */
  imagePublicId: string | null;
  /** Inventario disponible al momento de añadir (para warning si baja) */
  stockAtAdd: number | null;
  addedAt: number; // timestamp
}

interface CartState {
  items: CartItem[];
  hydrated: boolean;
  /** Añadir 1 unidad (suma qty si ya existe el variantId) */
  add: (item: Omit<CartItem, 'quantity' | 'addedAt'>, qty?: number) => void;
  /** Remover línea completa */
  remove: (variantId: string) => void;
  /** Actualizar quantity (si llega a 0, remove) */
  updateQty: (variantId: string, quantity: number) => void;
  /** Vaciar todo el carrito */
  clear: () => void;
  /** Total en centavos MXN (IVA incluido) */
  subtotalCents: () => number;
  /** Número de piezas (suma de quantities) */
  totalUnits: () => number;
  /** Helper para el icon del header */
  hasItems: () => boolean;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      hydrated: false,

      add: (item, qty = 1) => {
        const { items } = get();
        const existing = items.find((x) => x.variantId === item.variantId);
        if (existing) {
          set({
            items: items.map((x) =>
              x.variantId === item.variantId ? { ...x, quantity: x.quantity + qty } : x
            ),
          });
        } else {
          set({
            items: [...items, { ...item, quantity: qty, addedAt: Date.now() }],
          });
        }
      },

      remove: (variantId) => {
        set({ items: get().items.filter((x) => x.variantId !== variantId) });
      },

      updateQty: (variantId, quantity) => {
        const qty = Math.max(0, Math.floor(quantity));
        if (qty === 0) {
          get().remove(variantId);
          return;
        }
        set({
          items: get().items.map((x) => (x.variantId === variantId ? { ...x, quantity: qty } : x)),
        });
      },

      clear: () => set({ items: [] }),

      subtotalCents: () =>
        get().items.reduce((sum, x) => sum + x.unitPriceMxnCents * x.quantity, 0),

      totalUnits: () => get().items.reduce((sum, x) => sum + x.quantity, 0),

      hasItems: () => get().items.length > 0,
    }),
    {
      name: 'nqoro-cart-v1',
      storage: createJSONStorage(() => localStorage),
      // Solo persistimos los items; hydrated y funciones se recalculan en cliente
      partialize: (state) => ({ items: state.items }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    }
  )
);

/** Helpers de formato fuera del store para evitar re-renders. */
export function formatMxnCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
